import React from "react";
import Button, { ButtonProps } from "@mui/material/Button";
import useTheme from "../../common/hooks/useTheme";
import { Box } from "@mui/material";
import { SxProps } from "@mui/system";
import { Theme } from "@mui/material/styles";
import { Link } from "react-router-dom";

interface CustomButtonProps extends ButtonProps {
  label: string;
  icon?: React.ReactNode;
  sx?: SxProps<Theme>;
  isDisable?: boolean;
  /**
   * Render as a router link to this path instead of a plain button, so it
   * keeps native link behaviour (open in new tab, copy address).
   */
  to?: string;
}

const SecondaryButton: React.FC<CustomButtonProps> = ({
  onClick,
  label,
  sx,
  icon,
  isDisable = false,
  to,
  // Anything else (aria-label, title, type, …) goes straight to the button,
  // so an icon-only use can still carry an accessible name and a tooltip.
  ...rest
}) => {
  const theme = useTheme();

  return (
    <Button
      {...rest}
      {...(to ? { component: Link, to } : {})}
      onClick={onClick}
      sx={{
        height: "38px",
        border: `1px solid ${
          isDisable
            ? theme.colors_ds.NEUTRAL_BORDER_WEAK
            : theme.colors_ds.PRIMARY_ICON_MAIN
        }`,
        textTransform: "none",
        borderRadius: "8px",
        fontFamily: "var(--font-Medium)",
        fontSize: "14px",
        padding: label !== "" ? "8px 16px" : "8px 10px",
        backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
        color: theme.colors_ds.PRIMARY_ICON_MAIN,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        minWidth: label === "" ? "10px" : "auto", // Adjust width for icon-only buttons
        "&:hover": {
          backgroundColor: theme.colorTokens.P[10],
        },
        "&:active": {
          backgroundColor: theme.colorTokens.P[20],
        },
        "& .MuiTouchRipple-root": {
          color: theme.colorTokens.P[20],
        },
        ...sx, // Allow additional styles to be passed via props
      }}
      disabled={isDisable}
    >
      {icon && (
        <Box
          sx={{
            marginRight: label !== "" ? "8px" : "0px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
      )}
      {label !== "" && <Box style={{ flex: 1 }}>{label}</Box>}
    </Button>
  );
};

export default SecondaryButton;
